import React from "react";
import styled from "styled-components";
import styles from "styles/styles";
import { usePagination } from "../../../hooks/usePagination";
import { useSorting } from "../../../hooks/useSorting";
import Pagination from "./Pagination";

const StyledTable = styled.table`
    background-color: ${styles.colors.grey3};
    border-collapse: collapse;
    table-layout: auto;
    width: 100%;
`;

const TableHeader = styled.th<{ sortable?: boolean }>`
    text-align: left;
    font-size: 16px;
    font-weight: 500;
    padding: 1rem;
    cursor: ${(props) => (props.sortable ? "pointer" : "default")};
    white-space: nowrap;
`;

const SortIndicator = styled.span<{
    direction: "asc" | "desc";
}>`
    display: inline-block;
    position: relative;
    top: -2px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid rgb(62, 86, 96);
    margin-left: 5px;
    transition: transform 0.2s ease-in-out;
    transform: ${(props) => (props.direction === "desc" ? "rotate(180deg)" : "rotate(0deg)")};
`;

const TableRow = styled.tr<{ clickable?: boolean }>`
    background-color: ${styles.colors.white};
    border-bottom: 1px solid ${styles.colors.grey3};
    cursor: ${(p) => (p.clickable ? "pointer" : "default")};
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: ${(p) => (p.clickable ? styles.colors.lightGrey : styles.colors.white)};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.td`
    padding: 1rem;
    font-size: 16px;
`;

const TableContainer = styled.div`
    width: 100%;
`;

export interface TableColumn<T> {
    key: keyof T;
    header: string;
    sortable?: boolean;
    render?: (item: T) => React.ReactNode;
}

export interface TableProps<T extends object> {
    data: T[];
    columns: TableColumn<T>[];
    initialOrderBy: keyof T;
    onRowClick?: (item: T) => void;
}

const Table = <T extends object>({
    data,
    columns,
    initialOrderBy,
    onRowClick,
}: TableProps<T>): React.ReactElement => {
    const { sortedData, orderBy, orderDirection, handleSort } = useSorting(
        data,
        initialOrderBy,
        "desc"
    );

    const {
        paginatedData,
        totalItems,
        itemsPerPage: currentItemsPerPage,
        startIndex,
        endIndex,
        canGoToNextPage,
        canGoToPreviousPage,
        goToNextPage,
        goToPreviousPage,
        goToFirstPage,
        goToLastPage,
        onItemsPerPageChange,
    } = usePagination({
        data: sortedData,
        itemsPerPage: 10,
        initialPage: 1,
    });

    return (
        <TableContainer>
            <StyledTable>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <TableHeader
                                key={String(column.key)}
                                scope="col"
                                sortable={column.sortable}
                                onClick={() => column.sortable && handleSort(column.key)}
                            >
                                {column.header}
                                {orderBy === column.key && column.sortable && (
                                    <SortIndicator direction={orderDirection} />
                                )}
                            </TableHeader>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((item: T, index: number) => (
                        <TableRow
                            key={index}
                            clickable={!!onRowClick}
                            onClick={() => onRowClick?.(item)}
                        >
                            {columns.map((column) => (
                                <TableCell key={String(column.key)}>
                                    {column.render
                                        ? column.render(item)
                                        : String(item[column.key] || "")}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </tbody>
            </StyledTable>

            {totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    itemsPerPage={currentItemsPerPage}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    canGoToNextPage={canGoToNextPage}
                    canGoToPreviousPage={canGoToPreviousPage}
                    goToNextPage={goToNextPage}
                    goToPreviousPage={goToPreviousPage}
                    goToFirstPage={goToFirstPage}
                    goToLastPage={goToLastPage}
                    onItemsPerPageChange={onItemsPerPageChange}
                />
            )}
        </TableContainer>
    );
};

export default Table;
